// Masa, CG e inercias del cohete. Ver docs/theory/mass.md.
import { attachedFront, bodyRadiusAt, layoutStack, stackRadius, type Layout } from '../rocket/geometry';
import { getMaterial } from '../rocket/materials';
import type { MotorResolver } from '../rocket/motor';
import { listMounts, motorAftX, type Mount } from '../rocket/mounts';
import type { Attached, FinSet, InertComponent, InnerTube, NoseShoulder, Parachute, Rocket, StackComponent } from '../rocket/types';
import { combine, EMPTY_BODY, hollowCylinder, revolutionBody, shift, withMass, type RigidBody } from './rigid-body';

export interface MassOptions {
  /** Tiempo desde la ignición (s) para evaluar la masa del motor. Por defecto 0. */
  readonly motorTime?: number;
  /** Resolutor de motores. Sin él los motores no aportan masa y se emite un aviso. */
  readonly motors?: MotorResolver;
}

export interface MassResult {
  /** Todo menos los motores. */
  readonly structure: RigidBody;
  /** Solo los motores. */
  readonly motors: RigidBody;
  /** Estructura + motores. */
  readonly total: RigidBody;
  readonly warnings: readonly string[];
}

const density = (materialId: string): number => getMaterial(materialId)?.density ?? 0;

function stackBody(c: StackComponent): RigidBody {
  switch (c.type) {
    case 'nose-cone': {
      const body = revolutionBody((x) => stackRadius(c, x), c.length, density(c.material), c.wallThickness, c.filled || c.wallThickness >= c.baseDiameter / 2);
      const cone = c.massOverride === undefined ? body : withMass(body, c.massOverride);
      return c.shoulder === undefined ? cone : combine([cone, shift(shoulderBody(c.shoulder, density(c.material)), c.length)]);
    }
    case 'body-tube': {
      const ro = c.outerDiameter / 2;
      const ri = ro - c.wallThickness;
      const m = c.massOverride ?? density(c.material) * Math.PI * (ro * ro - ri * ri) * c.length;
      return hollowCylinder(ro, ri, c.length, m);
    }
    case 'transition': {
      const body = revolutionBody((x) => stackRadius(c, x), c.length, density(c.material), c.wallThickness, false);
      return c.massOverride === undefined ? body : withMass(body, c.massOverride);
    }
  }
}

/** Hombro: cilindro hueco con tapa opcional en el extremo trasero, medido desde su borde delantero. */
function shoulderBody(sh: NoseShoulder, rho: number): RigidBody {
  const ro = sh.diameter / 2;
  const t = Math.min(sh.thickness, ro);
  const ri = ro - t;
  const shell = hollowCylinder(ro, ri, sh.length, rho * Math.PI * (ro * ro - ri * ri) * sh.length);
  if (!sh.capped || ri === 0) return shell;
  const cap = shift(hollowCylinder(ro, 0, t, rho * Math.PI * ro * ro * t), Math.max(sh.length - t, 0));
  return combine([shell, cap]);
}

function finBody(f: FinSet, parent: Layout): RigidBody {
  const front = attachedFront(parent, f.rootChord, f.position);
  const area = ((f.rootChord + f.tipChord) * f.span) / 2;
  const mass = f.massOverride ?? density(f.material) * f.thickness * area * f.finCount;
  const sum = f.rootChord + f.tipChord;
  // Centroide axial del trapecio desde el borde de ataque de la raíz.
  const cx = sum === 0 ? 0 : (f.sweep * (f.rootChord + 2 * f.tipChord)) / (3 * sum) + (f.rootChord * f.rootChord + f.rootChord * f.tipChord + f.tipChord * f.tipChord) / (3 * sum);
  const rBody = bodyRadiusAt(parent, front + cx);
  const rc = rBody + (sum === 0 ? 0 : (f.span * (f.rootChord + 2 * f.tipChord)) / (3 * sum));
  const meanChord = sum / 2;
  return { mass, cgX: front + cx, izz: mass * rc * rc, iyy: mass * (rc * rc / 2 + (meanChord * meanChord) / 12) };
}

function innerTubeBody(m: InnerTube, parent: Layout): RigidBody {
  const front = attachedFront(parent, m.length, m.position);
  const ro = m.innerDiameter / 2 + m.wallThickness;
  const ri = m.innerDiameter / 2;
  const mass = m.massOverride ?? density(m.material) * Math.PI * (ro * ro - ri * ri) * m.length;
  return shift(hollowCylinder(ro, ri, m.length, mass), front);
}

function chuteBody(p: Parachute, parent: Layout): RigidBody {
  const front = attachedFront(parent, p.packedLength, p.position);
  const lines = p.lines === undefined ? 0 : p.lines.count * p.lines.length * density(p.lines.material);
  const mass = p.massOverride ?? density(p.material) * (Math.PI * p.diameter * p.diameter) / 4 + lines;
  // Cilindro macizo con el diámetro interior del tubo que lo contiene (ver docs/theory/mass.md).
  const c = parent.component;
  const inner = c.type === 'nose-cone' ? 0 : c.type === 'body-tube' ? c.outerDiameter / 2 - c.wallThickness : Math.min(c.foreDiameter, c.aftDiameter) / 2 - c.wallThickness;
  return shift(hollowCylinder(Math.max(inner, 0), 0, p.packedLength, mass), front);
}

/** Radio interior del cuerpo que contiene al componente. */
function parentInnerRadius(parent: Layout): number {
  const c = parent.component;
  return c.type === 'nose-cone' ? 0 : c.type === 'body-tube' ? c.outerDiameter / 2 - c.wallThickness : Math.min(c.foreDiameter, c.aftDiameter) / 2 - c.wallThickness;
}

function inertBody(a: InertComponent, parent: Layout): RigidBody {
  const front = attachedFront(parent, a.length, a.position);
  switch (a.type) {
    case 'shock-cord': {
      const mass = a.massOverride ?? (getMaterial(a.material)?.density ?? 0) * a.cordLength;
      return shift(hollowCylinder(Math.max(parentInnerRadius(parent), 0), 0, a.length, mass), front);
    }
    case 'mass-component':
      return shift(hollowCylinder(Math.max(parentInnerRadius(parent), 0), 0, a.length, a.massOverride ?? a.mass), front);
    case 'centering-ring':
    case 'bulkhead':
    case 'engine-block': {
      const ro = a.outerDiameter / 2;
      const ri = a.innerDiameter / 2;
      const mass = a.massOverride ?? density(a.material) * Math.PI * (ro * ro - ri * ri) * a.length;
      return shift(hollowCylinder(ro, ri, a.length, mass), front);
    }
    case 'launch-lug': {
      const ro = a.outerDiameter / 2;
      const ri = Math.max(ro - a.wallThickness, 0);
      const mass = a.massOverride ?? density(a.material) * Math.PI * (ro * ro - ri * ri) * a.length;
      return shift(hollowCylinder(ro, ri, a.length, mass), front);
    }
  }
}

function attachedBody(a: Attached, parent: Layout): RigidBody {
  switch (a.type) {
    case 'fin-set':
      return finBody(a, parent);
    case 'inner-tube':
      return innerTubeBody(a, parent);
    case 'parachute':
      return chuteBody(a, parent);
    default:
      return inertBody(a, parent);
  }
}

function motorBody(m: Mount, opts: MassOptions, warnings: string[]): RigidBody {
  const sel = m.mark.motor;
  if (sel === undefined) return EMPTY_BODY;
  const profile = opts.motors?.(sel.motorId);
  if (profile === undefined) {
    warnings.push(`No se encontró el motor ${sel.motorId} del soporte ${m.tube.id}: se ignora su masa`);
    return EMPTY_BODY;
  }
  const body = hollowCylinder(profile.diameter / 2, 0, profile.length, profile.massAt(opts.motorTime ?? 0));
  return shift(body, motorAftX(m) - profile.length);
}

export function computeMass(rocket: Rocket, opts: MassOptions = {}): MassResult {
  const warnings: string[] = [];
  const structure: RigidBody[] = [];

  for (const entry of layoutStack(rocket)) {
    structure.push(shift(stackBody(entry.component), entry.x0));
    if (entry.component.type === 'nose-cone') continue;
    for (const child of entry.component.children) structure.push(attachedBody(child, entry));
  }
  const motors = listMounts(rocket).map((m) => motorBody(m, opts, warnings));

  const s = combine(structure);
  const m = combine(motors.filter((b) => b.mass > 0));
  return { structure: s, motors: m, total: combine([s, m]), warnings };
}

export interface MotorPlacement {
  readonly mountId: string;
  readonly motorId: string;
  readonly ejectionDelay: number;
  /** Posición axial del extremo trasero del motor (extremo trasero del tubo portamotor más su voladizo), en m desde la punta. */
  readonly aftX: number;
}

/** Ubicación de cada motor instalado: a ras del extremo trasero de su tubo, más el voladizo. */
export function motorPlacements(rocket: Rocket): MotorPlacement[] {
  return listMounts(rocket).flatMap((m) => (m.mark.motor === undefined ? [] : [{ mountId: m.tube.id, motorId: m.mark.motor.motorId, ejectionDelay: m.mark.motor.ejectionDelay, aftX: motorAftX(m) }]));
}

export interface MassModel {
  /** Estructura (todo menos los motores); constante en el tiempo. */
  readonly structure: RigidBody;
  /** Masa total a `t` s de la ignición. */
  at(t: number): RigidBody;
}

/** Modelo de masa eficiente para la simulación: la estructura se calcula una sola vez. */
export function createMassModel(rocket: Rocket, motors: MotorResolver): MassModel {
  const structure = computeMass(rocket).structure;
  const installed = motorPlacements(rocket).flatMap((p) => {
    const profile = motors(p.motorId);
    return profile === undefined ? [] : [{ profile, aftX: p.aftX }];
  });
  return {
    structure,
    at(t: number): RigidBody {
      const parts: RigidBody[] = [structure];
      for (const { profile, aftX } of installed) {
        parts.push(shift(hollowCylinder(profile.diameter / 2, 0, profile.length, profile.massAt(t)), aftX - profile.length));
      }
      return combine(parts);
    },
  };
}
